import { useCallback, useState } from 'react';

import { getToken } from '@verantos-dev/core-auth';
import { getGatewayApiBase } from '@verantos-dev/core-constants';

/**
 * Hook to export ALL concepts from a preview as a CSV file.
 * The CSV is built server-side — the client just triggers a download.
 */
export const useExportPreviewConcepts = ({
  previewId,
  conceptGroupName,
  totalCount,
}: {
  previewId: string;
  conceptGroupName: string;
  totalCount: number;
}) => {
  const [exporting, setExporting] = useState(false);

  const handleExportAll = useCallback(async () => {
    if (!previewId || totalCount === 0) return;

    setExporting(true);
    try {
      const baseUrl = getGatewayApiBase();
      const token = await getToken();
      const response = await fetch(`${baseUrl}/concept-groups/preview/${previewId}/export`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      const filename = `${conceptGroupName}-validation-results.csv`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Preview export failed', error);
    } finally {
      setExporting(false);
    }
  }, [previewId, totalCount, conceptGroupName]);

  return { exporting, handleExportAll };
};
